import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Trophy, Star, Gift, Target, Crown, Info, X,
  Lock, Eye, Sparkles,
  ArrowRight, Flame, Gem, Shield, Swords, Fish
} from 'lucide-react'
import { useLoyaltyStore } from '../../stores/loyaltyStore'
import { ACHIEVEMENT_LEVELS } from '../../types/loyalty'
import type { AchievementLevel } from '../../types'
import { useAuthStore } from '../../stores'
import { Card, Button, Badge } from '../ui'
import EligibilityStatusBanner from './EligibilityStatusBanner'
import PremiumClaimModal from './PremiumClaimModal'

interface LoyaltyDashboardProps {
  onClose?: () => void
  className?: string
  embedded?: boolean // When true, removes container styling for embedding in other components
}

// Achievement level icons mapping
const LEVEL_ICONS = {
  explorer: Fish,
  hunter: Target,
  champion: Crown,
  diamond: Gem
} as const

// Mystery rewards previews (minimal information to create maximum curiosity)
const MYSTERY_PREVIEWS = {
  explorer: "████████ + ███████ + ???",
  hunter: "██████ ███████ + ███████ + ???",
  champion: "████████ ███████ + ████████ + ???",
  diamond: "██████ ███ + ███████ ██████████ + ???"
} as const

const LoyaltyDashboard = ({ onClose, className = '', embedded = false }: LoyaltyDashboardProps) => {
  const navigate = useNavigate()
  const { userLoyalty } = useLoyaltyStore()
  const { user } = useAuthStore()
  const [activeTab, setActiveTab] = useState<'overview' | 'achievements'>('overview')
  const [showRewardPreview, setShowRewardPreview] = useState<string | null>(null)
  const [claimModalOpen, setClaimModalOpen] = useState(false)
  const [selectedAchievement, setSelectedAchievement] = useState<any>(null)
  const [claimedAchievements, setClaimedAchievements] = useState<Set<string>>(new Set())

  // Load claimed achievements on mount
  useEffect(() => {
    const loadClaimedAchievements = async () => {
      if (!user) return

      try {
        const { prizeDeliveryService } = await import('../../services/prizeDeliveryService')
        const claimedLevels = await prizeDeliveryService.getUserLoyaltyDeliveries(user.id)
        setClaimedAchievements(new Set(claimedLevels))
      } catch (error) {
        console.error('Error loading claimed achievements:', error)
      }
    }

    loadClaimedAchievements()
  }, [user])

  // Handle button actions
  const handleViewRaffles = () => {
    onClose?.()
    navigate('/')
  }

  const handleViewRewards = () => {
    if (userLoyalty) {
      setActiveTab('achievements')
    } else {
      // For new users, just close modal since they will see the preview above
      onClose?.()
    }
  }

  const handleClaimRewards = async (level: AchievementLevel) => {
    const achievement = userLoyalty?.achievements.find(a => a.level === level && a.isUnlocked)
    if (!achievement || !achievement.canClaimPhysicalRewards || !user) return

    try {
      // Check if already claimed using optimized query
      const { prizeDeliveryService } = await import('../../services/prizeDeliveryService')
      const claimedLevels = await prizeDeliveryService.getUserLoyaltyDeliveries(user.id)
      const alreadyClaimed = claimedLevels.includes(level)

      if (alreadyClaimed) {
        // TODO: Show toast notification that it's already claimed
        console.log('Already claimed this achievement')
        return
      }

      setSelectedAchievement(achievement)
      setClaimModalOpen(true)
    } catch (error) {
      console.error('Error checking existing claims:', error)
      // Allow to continue in case of error
      setSelectedAchievement(achievement)
      setClaimModalOpen(true)
    }
  }

  const handleConfirmClaim = async (achievement: any, shippingData: any) => {
    if (!user) return

    try {
      const { prizeDeliveryService } = await import('../../services/prizeDeliveryService')
      await prizeDeliveryService.createPrizeDelivery({
        raffleId: 'loyalty-rewards',
        userId: user.id,
        winnerId: `loyalty-${user.id}-${achievement.level}`,
        ticketId: `loyalty-achievement-${achievement.level}`,
        winningNumber: achievement.level,
        prizeType: 'main',
        modalityType: 'loyalty-reward',
        totalPrizeValue: achievement.physicalRewards?.reduce((sum: number, reward: any) => sum + (reward.value || 0), 0) || 0,
        deliveryConfig: {
          frequency: 'single',
          count: 1,
          items: [{
            title: `Recompensas ${achievement.name}`,
            description: `Logro de loyalty nivel ${achievement.level}`,
            estimatedValue: achievement.physicalRewards?.reduce((sum: number, reward: any) => sum + (reward.value || 0), 0) || 0,
            items: achievement.physicalRewards?.map((reward: any) => reward.name) || []
          }]
        },
        shippingAddress: {
          firstName: shippingData.firstName,
          lastName: shippingData.lastName,
          addressLine1: shippingData.address,
          number: '',
          addressLine2: '',
          city: shippingData.city,
          state: shippingData.city,
          postalCode: shippingData.postalCode,
          country: shippingData.country,
          phone: shippingData.phone,
          email: shippingData.email,
          isDefault: false
        }
      })

      console.log('Delivery created successfully for loyalty reward:', achievement.level)

      // Mark as claimed
      setClaimedAchievements(prev => new Set(prev).add(achievement.level))

      // Close the modal
      setClaimModalOpen(false)
      setSelectedAchievement(null)

    } catch (error) {
      console.error('Error creating delivery:', error)
      throw error
    }
  }

  if (!userLoyalty) {
    return (
      <div className={`bg-white rounded-3xl shadow-2xl overflow-hidden max-w-4xl ${className}`}>
        {/* Motivational Header for New Users */}
        <div className="relative p-8 text-white overflow-hidden bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-800">
          <div className="absolute inset-0 opacity-20">
            <div className="w-full h-full" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundRepeat: 'repeat'
            }} />
          </div>

          <div className="relative z-10 text-center">
            <div className="mb-6">
              <Sparkles className="w-16 h-16 text-emerald-200 mx-auto mb-4" />
            </div>
            <h1 className="text-4xl font-bold mb-4">¡Bienvenido a Wild Draw Loyalty!</h1>
            <p className="text-xl text-emerald-100 mb-8">
              Tu aventura carpista está a punto de comenzar. Cada compra te acerca a recompensas increíbles.
            </p>
          </div>
        </div>

        {/* Preview of what they can unlock */}
        <div className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Descubre lo que puedes desbloquear
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {Object.entries(ACHIEVEMENT_LEVELS).map(([level, achievement]) => {
              return (
                <div
                  key={level}
                  className="relative p-6 rounded-2xl border-2 border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 rounded-xl bg-gray-200 text-gray-500">
                      <Lock className="w-6 h-6" />
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {achievement.pointsRequired} puntos
                    </Badge>
                  </div>

                  <h3 className="text-xl font-bold text-gray-600 mb-2">
                    Cofre {achievement.name}
                  </h3>

                  <p className="text-sm text-gray-500 mb-4">
                    {achievement.description}
                  </p>

                  <div className="text-xs text-gray-400">
                    <div className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      <span>{MYSTERY_PREVIEWS[level as AchievementLevel]}</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Call to Action */}
          <div className="bg-gradient-to-r from-emerald-50 via-blue-50 to-emerald-50 border-2 border-emerald-200 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              ¡Haz tu primera compra y comienza a ganar puntos!
            </h3>
            <p className="text-gray-600 mb-6">
              Con solo <strong>€1 = 1 punto</strong>. Tu primer logro "Explorador" te espera a solo 500 puntos.
            </p>
            <div className="flex justify-center">
              <Button
                size="lg"
                className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 flex items-center gap-2"
                onClick={handleViewRaffles}
              >
                <Target className="w-5 h-5" />
                Ver Sorteos Activos
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Get next achievement level
  const getNextLevel = (): AchievementLevel | null => {
    const levels = Object.keys(ACHIEVEMENT_LEVELS) as AchievementLevel[]
    const unlockedLevels = userLoyalty.achievements.filter(a => a.isUnlocked).map(a => a.level)
    return levels.find(level => !unlockedLevels.includes(level)) || null
  }

  const nextLevel = getNextLevel()
  const currentLevel = userLoyalty.currentLevel
  const pointsToNext = nextLevel ? ACHIEVEMENT_LEVELS[nextLevel].pointsRequired - userLoyalty.totalPoints : 0


  const tabs = [
    { id: 'overview', label: 'Cofres Misteriosos', icon: Trophy },
    { id: 'achievements', label: 'Mi Progreso', icon: Star }
  ] as const

  // Mystery chest component for locked achievements
  const MysteryChest = ({ level, achievement, isUnlocked, isNext }: {
    level: AchievementLevel
    achievement: typeof ACHIEVEMENT_LEVELS[AchievementLevel]
    isUnlocked: boolean
    isNext: boolean
  }) => {
    const Icon = LEVEL_ICONS[level]
    const progress = Math.min(100, (userLoyalty.totalPoints / achievement.pointsRequired) * 100)

    return (
      <div
        className={`
          relative p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl border-2 transition-all duration-300 cursor-pointer
          ${isUnlocked
            ? 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-300'
            : isNext
              ? 'bg-gradient-to-br from-amber-50 via-amber-50 to-orange-100 border-amber-300 shadow-lg'
              : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'
          }
        `}
        onMouseEnter={() => !isUnlocked && setShowRewardPreview(level)}
        onMouseLeave={() => setShowRewardPreview(null)}
        role="button"
        tabIndex={0}
        aria-label={`${achievement.name} ${isUnlocked ? 'desbloqueado' : `requiere ${achievement.pointsRequired} puntos`}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            if (!isUnlocked) setShowRewardPreview(showRewardPreview === level ? null : level)
          }
        }}
      >

        {/* Lock/Unlock indicator */}
        <div className="flex items-center justify-between mb-4">
          <div
            className={`p-3 rounded-xl ${
              isUnlocked
                ? 'bg-emerald-200 text-emerald-700'
                : isNext
                  ? 'bg-amber-200 text-amber-700 shadow-lg'
                  : 'bg-gray-200 text-gray-500'
            }`}
          >
            {isUnlocked ? <Icon className="w-6 h-6" /> : <Lock className="w-6 h-6" />}
          </div>

          {isNext && !isUnlocked && (
            <Badge variant="danger" className="flex items-center gap-1">
              <Flame className="w-3 h-3" />
              ¡Siguiente!
            </Badge>
          )}
        </div>

        {/* Level name and description */}
        <h3 className={`text-base sm:text-lg md:text-xl font-bold mb-2 ${
          isUnlocked ? 'text-emerald-800' : isNext ? 'text-amber-800' : 'text-gray-600'
        }`}>
          {isUnlocked ? achievement.name : `Cofre ${achievement.name}`}
        </h3>

        <p className={`text-xs sm:text-sm mb-3 sm:mb-4 ${
          isUnlocked ? 'text-emerald-700' : isNext ? 'text-amber-700' : 'text-gray-500'
        }`}>
          {isUnlocked
            ? achievement.description
            : `${achievement.pointsRequired} puntos para desbloquear`
          }
        </p>

        {/* Progress bar for locked achievements */}
        {!isUnlocked && (
          <div className="mb-4">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-600">Progreso</span>
              <span className="font-medium text-gray-800">
                {userLoyalty.totalPoints} / {achievement.pointsRequired}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className={`h-2 rounded-full transition-all duration-1000 ease-out ${
                  isNext
                    ? 'bg-gradient-to-r from-amber-400 to-orange-500'
                    : 'bg-gradient-to-r from-gray-400 to-gray-500'
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Reward preview and claim button */}
        <div className={`text-xs ${
          isUnlocked ? 'text-emerald-600' : isNext ? 'text-amber-600' : 'text-gray-400'
        }`}>
          {isUnlocked ? (
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Crown className="w-3 h-3" />
                <span>Recompensas desbloqueadas</span>
              </div>
              {/* Claim button for unlocked achievements */}
              {claimedAchievements.has(level) ? (
                <Button
                  size="sm"
                  disabled
                  className="w-full bg-gray-400 text-white text-xs cursor-not-allowed px-2 py-1"
                >
                  <Crown className="w-3 h-3 mr-1 flex-shrink-0" />
                  <span className="truncate">Ya Reclamado</span>
                </Button>
              ) : (
                <Button
                  onClick={() => handleClaimRewards(level)}
                  size="sm"
                  className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white text-xs px-2 py-1"
                >
                  <Gift className="w-3 h-3 mr-1 flex-shrink-0" />
                  <span className="truncate">Reclamar Recompensas</span>
                </Button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              <span>{MYSTERY_PREVIEWS[level]}</span>
            </div>
          )}
        </div>

        {/* Hover preview for rewards */}
        {showRewardPreview === level && !isUnlocked && (
          <div className="absolute inset-x-0 top-full mt-2 p-4 bg-white rounded-xl shadow-xl border-2 border-amber-200 z-10 transition-opacity duration-200">
              <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Cofre Misterioso
              </h4>
              <div className="space-y-2 text-xs text-gray-600">
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200 relative">
                  <Gift className="w-3 h-3 text-gray-400" />
                  <span className="blur-sm select-none text-gray-400">████████ ███</span>
                  <Lock className="w-3 h-3 text-gray-400 absolute right-2" />
                </div>
                <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200 relative">
                  <Gift className="w-3 h-3 text-gray-400" />
                  <span className="blur-sm select-none text-gray-400">██████ ████████</span>
                  <Lock className="w-3 h-3 text-gray-400 absolute right-2" />
                </div>
                <div className="flex items-center gap-2 p-2 bg-amber-50 rounded border border-amber-200">
                  <Sparkles className="w-3 h-3 text-amber-600" />
                  <span className="text-amber-700 font-medium">+ Sorpresas Exclusivas</span>
                </div>
              </div>
            </div>
        )}
      </div>
    )
  }

  return (
    <div className={`${embedded ? 'w-full bg-white overflow-hidden' : 'bg-white rounded-xl sm:rounded-2xl lg:rounded-3xl shadow-2xl overflow-hidden w-full max-w-sm sm:max-w-2xl lg:max-w-4xl'} ${className}`}>
      {/* Dramatic Header */}
      <div className={`relative ${embedded ? 'p-3 sm:p-4' : 'p-4 sm:p-6'} text-white overflow-hidden bg-gradient-to-br from-slate-800 via-slate-700 to-emerald-800`}>
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat'
          }} />
        </div>

        <div className="relative z-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4">
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              <div className="flex-shrink-0">
                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-emerald-400" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-1 truncate">Wild Draw Loyalty</h1>
                <p className="text-emerald-300 text-xs sm:text-sm md:text-base truncate">
                  {currentLevel ? `Nivel ${ACHIEVEMENT_LEVELS[currentLevel].name}` : 'Aventurero en Ciernes'}
                </p>
              </div>
            </div>

            {!embedded && onClose && (
              <Button
                onClick={onClose}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 self-start"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </Button>
            )}
          </div>


          {/* Current Status */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 sm:gap-3 md:gap-4">
            <Card className="bg-white/10 border-white/20 text-white p-2 sm:p-3 md:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <Trophy className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-emerald-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-emerald-300">Puntos Totales</p>
                  <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold truncate">{userLoyalty.totalPoints.toLocaleString()}</p>
                </div>
              </div>
            </Card>

            {nextLevel && (
              <Card className="bg-white/10 border-white/20 text-white p-2 sm:p-3 md:p-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <Target className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-amber-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-amber-300 truncate">Para {ACHIEVEMENT_LEVELS[nextLevel].name}</p>
                    <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold">{pointsToNext}</p>
                  </div>
                </div>
              </Card>
            )}

            <Card className="bg-white/10 border-white/20 text-white p-2 sm:p-3 md:p-4 col-span-1 sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-2 sm:gap-3">
                <Trophy className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-amber-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-amber-300">Achievements</p>
                  <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold">{userLoyalty.achievements.filter(a => a.isUnlocked).length}/4</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Eligibility Status Banner */}
      <div className={`${embedded ? 'px-3 py-2 sm:px-4 sm:py-3' : 'px-4 sm:px-6 py-3 sm:py-4'} bg-gray-50`}>
        <EligibilityStatusBanner
          userLoyalty={userLoyalty}
          variant="compact"
          className="mb-0"
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="flex">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex-1 flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-4 py-3 sm:py-4
                  text-sm sm:text-base font-medium transition-colors border-b-3
                  ${isActive
                    ? 'text-emerald-600 border-emerald-600 bg-white'
                    : 'text-gray-600 border-transparent hover:text-emerald-600 hover:bg-emerald-50'
                  }
                `}
                role="tab"
                aria-selected={isActive}
                aria-controls={`${tab.id}-panel`}
              >
                <Icon className="w-5 h-5" />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className={`${embedded ? 'p-2 sm:p-3' : 'p-3 sm:p-4 md:p-6'}`}>
        {/* Overview Tab - Mystery Chests */}
        {activeTab === 'overview' && (
          <div
            id="overview-panel"
            role="tabpanel"
            className="transition-opacity duration-300"
          >

            {/* Mystery Chests Grid */}
            <div className="mb-6 sm:mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
                  <span className="hidden sm:inline">Cofres de Recompensas Misteriosas</span>
                  <span className="sm:hidden">Cofres Misteriosos</span>
                </h3>
                <Badge variant="secondary" className="flex items-center gap-1 self-start sm:self-auto text-xs sm:text-sm whitespace-nowrap">
                  <Shield className="w-3 h-3 flex-shrink-0" />
                  <span>{userLoyalty.achievements.filter(a => a.isUnlocked).length}/4 Desbloqueados</span>
                </Badge>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:gap-6">
                {Object.entries(ACHIEVEMENT_LEVELS).map(([level, achievement]) => {
                  const isUnlocked = userLoyalty.achievements.some(
                    a => a.level === level && a.isUnlocked
                  )
                  const isNext = level === nextLevel

                  return (
                    <MysteryChest
                      key={level}
                      level={level as AchievementLevel}
                      achievement={achievement}
                      isUnlocked={isUnlocked}
                      isNext={isNext}
                    />
                  )
                })}
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-emerald-50 via-blue-50 to-emerald-50 border-2 border-emerald-200 rounded-xl sm:rounded-2xl p-4 sm:p-6">
              <div className="text-center">
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                  ¿Listo para desbloquear tu próximo cofre?
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  {nextLevel
                    ? `Solo te faltan ${pointsToNext} puntos para desbloquear ${ACHIEVEMENT_LEVELS[nextLevel].name}`
                    : "¡Has desbloqueado todos los cofres! Mantente atento a nuevos niveles."
                  }
                </p>
                <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 justify-center">
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 flex items-center justify-center gap-2 text-sm sm:text-base"
                    onClick={handleViewRaffles}
                  >
                    <Swords className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                    <span>Ver Sorteos Activos</span>
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 flex items-center justify-center gap-2 text-sm sm:text-base"
                    onClick={handleViewRewards}
                  >
                    <Crown className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                    <span>Explorar Recompensas</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Achievements Tab - Detailed Progress */}
        {activeTab === 'achievements' && (
          <div
            id="achievements-panel"
            role="tabpanel"
            className="transition-opacity duration-300"
          >
            <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-600" />
              Mi Progreso Detallado
            </h3>

            <div className="space-y-6">
              {Object.entries(ACHIEVEMENT_LEVELS).map(([level, achievement]) => {
                const userAchievement = userLoyalty.achievements.find(
                  a => a.level === level
                )
                const isUnlocked = userAchievement?.isUnlocked || false
                const progress = Math.min(100, (userLoyalty.totalPoints / achievement.pointsRequired) * 100)
                const Icon = LEVEL_ICONS[level as AchievementLevel]

                return (
                  <Card
                    key={level}
                    className={`p-6 border-2 transition-all ${
                      isUnlocked
                        ? 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-emerald-100'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      <div
                        className={`p-3 sm:p-4 rounded-xl ${
                          isUnlocked
                            ? 'bg-emerald-200 text-emerald-700'
                            : 'bg-gray-200 text-gray-500'
                        }`}
                      >
                        <Icon className="w-6 h-6 sm:w-8 sm:h-8" />
                      </div>

                      <div className="flex-1 w-full">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2">
                          <h4 className="text-xl sm:text-2xl font-bold text-gray-900">
                            {achievement.name}
                          </h4>
                          {isUnlocked && (
                            <Badge variant="success" className="flex items-center gap-1 self-start sm:self-auto">
                              <Crown className="w-4 h-4" />
                              Desbloqueado
                            </Badge>
                          )}
                        </div>

                        <p className="text-gray-600 mb-4">
                          {achievement.description}
                        </p>

                        <div className="mb-4">
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-600">Progreso</span>
                            <span className="font-medium">
                              {userLoyalty.totalPoints.toLocaleString()} / {achievement.pointsRequired.toLocaleString()} puntos
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                            <div
                              className={`h-3 rounded-full transition-all duration-1000 ease-out ${
                                isUnlocked
                                  ? 'bg-gradient-to-r from-emerald-400 to-emerald-600'
                                  : 'bg-gradient-to-r from-blue-400 to-blue-600'
                              }`}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <div className="text-right text-sm text-gray-500 mt-1">
                            {progress.toFixed(1)}% completado
                          </div>
                        </div>

                        {/* Rewards list */}
                        <div className="border-t pt-4">
                          <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <Gift className="w-4 h-4" />
                            Recompensas {isUnlocked ? 'Desbloqueadas' : 'Misteriosas'}
                          </h5>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {isUnlocked ? (
                              // Show actual rewards for unlocked achievements
                              achievement.physicalRewards.map((reward, i) => (
                                <div
                                  key={i}
                                  className="p-2 sm:p-3 rounded-lg border text-sm bg-emerald-50 border-emerald-200 text-emerald-800"
                                >
                                  <div className="font-medium text-xs sm:text-sm">{reward.name}</div>
                                  <div className="text-xs opacity-75 hidden sm:block">{reward.description}</div>
                                </div>
                              ))
                            ) : (
                              // Show mystery placeholders for locked achievements
                              <>
                                {achievement.physicalRewards.slice(0, 2).map((reward, i) => (
                                  <div
                                    key={i}
                                    className="p-2 sm:p-3 rounded-lg border text-sm bg-gray-50 border-gray-200 text-gray-400 relative overflow-hidden"
                                  >
                                    <div className="font-medium blur-sm select-none text-xs sm:text-sm">{reward.name}</div>
                                    <div className="text-xs opacity-50 blur-sm select-none hidden sm:block">{reward.description}</div>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <Lock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                                    </div>
                                  </div>
                                ))}
                                <div className="p-2 sm:p-3 rounded-lg border text-sm bg-amber-50 border-amber-200 text-amber-700 flex items-center gap-2 sm:col-span-2 lg:col-span-1">
                                  <Sparkles className="w-3 h-3 flex-shrink-0" />
                                  <div className="font-medium text-xs sm:text-sm">+ Sorpresas Exclusivas</div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {isUnlocked && userAchievement?.unlockedAt && (
                          <div className="flex items-center gap-2 text-sm text-emerald-600 mt-4 pt-4 border-t border-emerald-200">
                            <Info className="w-4 h-4" />
                            <span>
                              Desbloqueado el {new Intl.DateTimeFormat('es-ES', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              }).format(userAchievement.unlockedAt)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                )
              })}
            </div>
          </div>
        )}
      </div>

      {/* Premium Claim Modal */}
      <PremiumClaimModal
        isOpen={claimModalOpen}
        onClose={() => setClaimModalOpen(false)}
        achievement={selectedAchievement}
        user={user}
        onConfirmClaim={handleConfirmClaim}
      />
    </div>
  )
}

export default LoyaltyDashboard